/**
 * Server role names -> this app's RBAC roles.
 *
 * The backend returns one role per product: `FeaturesData.CCA.role_name` and
 * `FeaturesData.EIA.role_name`. Those strings are the server's vocabulary;
 * security/permissions.js has its own. This is the single translation point, so
 * a new server role is one line here rather than a string comparison sprinkled
 * through components.
 *
 * THE TRANSLATION IS VARIANT-AWARE, because the products genuinely disagree
 * about who may see Analytics:
 *
 *   server role              CCA                      EIA
 *   ----------------------   ----------------------   ----------------------
 *   super_admin              admin + analytics        admin          (no analytics)
 *   super_admin_analytics    admin + analytics        admin + analytics
 *   admin                    org_admin                org_admin
 *   anything else / absent   (baseline only)          (baseline only)
 *
 * Every signed-in user also holds the baseline `user` role. That is not a
 * guess: legacy gates the ordinary pages (Cost Advice, Telemetry, the portfolio
 * views) on authentication and FEATURE FLAGS, never on role_name — only the
 * three entries above are role-gated. Withholding the baseline from a user
 * whose role string we do not recognise would lock them out of the whole
 * product over a vocabulary mismatch.
 *
 * That asymmetry is legacy's, reproduced deliberately: Headerbar.vue accepts
 * ["super_admin","super_admin_analytics"] for CCA (:366, :415) but only
 * "super_admin_analytics" for EIA (:375, :422). The same rule is written twice
 * per variant, which is why it reads as intended rather than as a slip. If the
 * product owner says otherwise, this table is the one place to change it.
 *
 * Three legacy bugs are fixed rather than carried:
 *  1. Comparisons are case-insensitive everywhere. Legacy lowercases in
 *     Headerbar but NOT in Portfolios.vue:6/:13 (`applicationRoles !== 'admin'`),
 *     so a backend sending "Admin" silently loses the Org Portfolios privilege.
 *  2. A missing role cookie cannot throw. Legacy's Headerbar.vue:366/:375 do
 *     `(Cookies.get("CCA_role")).toLowerCase()` with no optional chaining, so a
 *     role-less user takes a TypeError that kills the whole menu.
 *  3. No cross-product leak. Legacy's Headerbar.vue:348 has an operator
 *     precedence bug — `&&` binds tighter than `||` — so a super_admin_analytics
 *     in one product is treated as privileged in the other. Reading only the
 *     active variant's branch makes that impossible.
 */

/**
 * Server `role_name` (lower-cased) -> app roles, per variant.
 *
 * Listed per product rather than as one map plus exceptions, because the
 * difference IS the point and a shared map with an `if (mode === 'eia')` patch
 * is exactly how that difference gets lost in a later edit.
 */
/** Held by every signed-in user, whatever the server calls their role. */
const BASELINE_ROLES = Object.freeze(['user']);

const ROLE_MAP = Object.freeze({
  cca: Object.freeze({
    super_admin: ['admin', 'analytics'],
    super_admin_analytics: ['admin', 'analytics'],
    admin: ['org_admin'],
  }),
  eia: Object.freeze({
    // No 'analytics': EIA withholds it from super_admin.
    super_admin: ['admin'],
    super_admin_analytics: ['admin', 'analytics'],
    admin: ['org_admin'],
  }),
});

/**
 * Pick the roles that apply to the product currently being served.
 *
 * @param {object|null} featuresData the server's { CCA, EIA } object
 * @param {'cca'|'eia'} mode
 * @returns {{ roles: string[], features: object|null, roleName: string|null }}
 */
export function resolveRoles(featuresData, mode) {
  const key = String(mode ?? '').toLowerCase();
  // Only the active product's branch is ever read — see bug 3 above.
  const branch = featuresData?.[key.toUpperCase()] ?? null;
  const roleName = branch?.role_name ?? null;

  if (!roleName) {
    return { roles: [...BASELINE_ROLES], features: branch, roleName: null };
  }

  const mapped = ROLE_MAP[key]?.[String(roleName).trim().toLowerCase()];
  if (!mapped) {
    console.warn(
      `[auth] Unknown server role "${roleName}" for ${key.toUpperCase()} — baseline access ` +
        'only. Add it to ROLE_MAP in features/auth/roles.js.',
    );
    return { roles: [...BASELINE_ROLES], features: branch, roleName };
  }

  return { roles: [...BASELINE_ROLES, ...mapped], features: branch, roleName };
}
